import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDealerAuth } from '../../context/DealerAuthContext'
import { apiError } from '../../../admin/api/apiError'
import AuthCard from '../../components/AuthCard/AuthCard'

// уведомления, с которыми на логин приходят из других экранов (?reason= / ?notice=)
const REASON_KEYS: Record<string, string> = {
  session_expired: 'dealer.login.notice_session_expired',
  branch_suspended: 'dealer.login.error_branch_suspended',
}
const NOTICE_KEYS: Record<string, string> = {
  password_reset: 'dealer.login.notice_password_reset',
  password_set: 'dealer.login.notice_password_set',
}

export default function LoginPage() {
  const { login, me } = useDealerAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const params = new URLSearchParams(location.search)
  const reasonKey = REASON_KEYS[params.get('reason') ?? '']
  const noticeKey = NOTICE_KEYS[params.get('notice') ?? '']
  // возвращаем туда, откуда увели на логин, вместе с query (например, /requests?delivery=NOT_SENT)
  const fromLocation = (location.state as { from?: Location })?.from
  const from = fromLocation ? fromLocation.pathname + (fromLocation.search ?? '') : '/'

  useEffect(() => {
    if (me) navigate(from, { replace: true })
  }, [me, navigate, from])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim() || !password) {
      setError(t('dealer.login.error_empty'))
      return
    }
    setError('')
    setLoading(true)
    try {
      await login({ email: email.trim(), password }, remember)
      navigate(from, { replace: true })
    } catch (err) {
      const { status, code } = apiError(err)
      if (code === 'INVALID_CREDENTIALS' || status === 401) setError(t('dealer.login.error_invalid'))
      else if (code === 'BRANCH_SUSPENDED') setError(t('dealer.login.error_branch_suspended'))
      else if (!status) setError(t('dealer.common.error_network'))
      else setError(t('dealer.login.error_failed'))
      console.error('[ERROR] Dealer login failed:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthCard title={t('dealer.login.title')} subtitle={t('dealer.login.subtitle')}>
      {noticeKey && <div className="dealer-alert dealer-alert--success">{t(noticeKey)}</div>}
      {!noticeKey && reasonKey && <div className="dealer-alert dealer-alert--info">{t(reasonKey)}</div>}

      <form className="dealer-auth__form" onSubmit={handleSubmit} noValidate>
        <div className="dealer-field">
          <label className="dealer-label" htmlFor="dealer-login-email">{t('dealer.login.label_email')}</label>
          <input
            id="dealer-login-email"
            className={`dealer-input${error ? ' dealer-input--error' : ''}`}
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError('') }}
            autoFocus
            autoComplete="username"
            inputMode="email"
            disabled={loading}
          />
        </div>
        <div className="dealer-field">
          <label className="dealer-label" htmlFor="dealer-login-password">{t('dealer.login.label_password')}</label>
          <input
            id="dealer-login-password"
            className={`dealer-input${error ? ' dealer-input--error' : ''}`}
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError('') }}
            autoComplete="current-password"
            disabled={loading}
          />
        </div>
        <div className="dealer-auth__row dealer-auth__row--between">
          <label className="dealer-checkbox" htmlFor="dealer-login-remember">
            <input
              id="dealer-login-remember"
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              disabled={loading}
            />
            <span>{t('dealer.login.remember_me')}</span>
          </label>
          <Link to="/forgot-password" className="dealer-link">{t('dealer.login.forgot_link')}</Link>
        </div>

        {error && <div className="dealer-alert dealer-alert--error" role="alert">{error}</div>}

        <button className="dealer-btn dealer-btn--primary dealer-btn--block" type="submit" disabled={loading}>
          {loading ? t('dealer.login.signing_in') : t('dealer.login.sign_in')}
        </button>
      </form>
    </AuthCard>
  )
}
